<template>
    <!-- Markel Direct "Please wait" loader: shown while the tenant schema chunk + saved risk data
         load, and while the first step builds (there is nothing to paint behind it until then). -->
    <LoaderOverlay
        :show="!journeyReady || !stepReady"
        title="Please wait"
        :text="journeyReady ? 'Loading your questions…' : 'We’re getting your quote ready. This won’t take long.'" />

    <div class="row" v-if="journeyReady">
        <div class="col-sm-12">
            <!-- Refer a friend descoped — hidden (2026-10-20)
            <div v-if="isReferral" class="quote-referral-banner" role="status">
                <em class="far fa-gift"></em>
                <span>You've been referred by a friend. Buy your policy and you'll both receive a &pound;40 voucher.</span>
            </div>
            -->
            <QuoteNav ref="navRef" :tabController="tabController" :default-tab="resumeTabId || tabController[0].id" :persist-key="stepStorageKey" :tracker-stages="trackerStages">
              <template v-for="tab in tabController" :key="tab.id" #[tab.id]>
                  <template v-if="navRef?.currentTabId === tab.id">
                    <!-- Building a step's form is one long synchronous job; the "Please wait" loader
                         above stays up until the browser has painted it (see stepReady). -->
                    <template v-if="stepReady">
                    <!--Quote Display (single-scheme confirmation renders review mode under review)-->
                   <component
                        :is="quoteDisplayComponent"
                        v-if="tab.id === 'quote-display' || (tab.id === 'confirmation' && isSingleScheme)"
                        :mode="tab.id === 'confirmation' ? (isReview ? 'review' : 'confirmed') : 'quote'"
                        :productId="props.productId"
                        :action="action"
                        :quoteId="quoteId"
                        :allowAdminFeeEdit="currentTenantConfig?.allowAdminFeeEdit ?? false"
                        :showPrintResults="currentTenantConfig?.showPrintResults ?? true"
                        :showSchemeTitles="currentTenantConfig?.showSchemeTitles ?? true"
                        @nav-back="navRef?.goBack()"
                        @nav-next="navRef?.goNext()"
                        @enter-review="navRef?.show(tabController[0].id)"
                    />
                    <ConfirmationTab
                        v-else-if="tab.id === 'confirmation'"
                        :action="action"
                        :productId="props.productId"
                        @nav-back="navRef?.goBack()"
                        @nav-next="navRef?.goNext()"
                        @nav-to="navRef?.show($event)"
                    />
                    <AddonsTab
                        v-else-if="tab.id === 'addons'"
                        :action="action"
                        :productId="props.productId"
                        :quoteId="quoteId"
                        @nav-back="navRef?.goBack()"
                        @nav-next="navRef?.goNext()"
                        @show-operator-script="showOperatorScriptForTab($event)"
                    />
                    <!-- Registration interstitial (customersite-only, between quote display and additional info). -->
                    <Registration
                        v-else-if="tab.id === 'registration'"
                        @nav-next="navRef?.goNext()"
                        @nav-back="navRef?.goBack()"
                    />
                    <!-- Additional questions (single scheme): the form sits beside a sticky quote
                         summary, mirroring the Markel Direct quote-display two-column layout. -->
                    <div v-else-if="tab.id === 'assumptions' && isSingleScheme" class="quote-additional">
                        <div class="ra-cols">
                            <div class="ra-main">
                                <QuestionSet
                                    :key="tab.id"
                                    v-bind="questionSetProps(tab)"
                                    @nav-back="navRef?.goBack()"
                                    @nav-next="navRef?.goNext()"
                                    @submit-initial="handleInitialSubmit($event, triggersQuote(tab.id))"
                                />
                            </div>
                            <aside class="ra-side">
                                <QuoteSummary readonly title="Summary" />
                            </aside>
                        </div>
                        <!-- Mobile only: sticky bottom summary dock (the sidebar aside is hidden at this width). -->
                        <AdditionalSummaryDock />
                    </div>
                    <QuestionSet
                        v-else-if="tab.schemaKey"
                        :key="tab.id"
                        v-bind="questionSetProps(tab)"
                        @nav-back="navRef?.goBack()"
                        @nav-next="navRef?.goNext()"
                        @submit-initial="handleInitialSubmit($event, triggersQuote(tab.id))"
                    />
                    </template>
                  </template>
              </template>
            </QuoteNav>
        </div>
    </div>

    <OperatorScriptModal
      v-if="tempOperatorScriptVue"
      :modelValue="operatorScript.visible.value"
      :script="operatorScript.script.value"
      @accept="operatorScript.accept"
      @decline="operatorScript.decline"
    />
</template>

<script lang="ts" setup>
import { useRiskStore  } from '@/stores/riskData'
import { storeToRefs } from 'pinia'
import { ref, watch, computed, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue'
import type { Pinia } from 'pinia'
import { useQuoteProductConfig } from './composables/useQuoteProductConfig';
import QuoteNav from '../QuoteNav.vue';
import QuestionSet from './QuestionSet/index.vue';
import Registration from './Registration/index.vue';
import QuoteSummary from './QuoteTab/SingleScheme/QuoteSummary.vue';
import AdditionalSummaryDock from './QuoteTab/SingleScheme/AdditionalSummaryDock.vue';
import { isCustomerSite } from '@/formEnvironment';
import QuoteTab from './QuoteTab/index.vue';
import QuoteTabSingleScheme from './QuoteTab/SingleScheme/index.vue';
import AddonsTab from './AddonsTab/index.vue';
import ConfirmationTab from './ConfirmationTab/index.vue';
import { useI18n } from 'vue-i18n';
import { DoQuote, SaveQuoteStage, ApiError } from '@/composables/useQuoteAPIRequests'
import { useQuoteErrors } from '@/composables/useQuoteErrors'
import { generateEmptyModel } from '@/utils/generateEmptyModel'
import { canonicaliseSchemaValues } from '@/utils/canonicaliseSchemaValues'
import { additionalOnlyKeys as computeAdditionalOnlyKeys, fingerprintRisk } from '@/utils/riskFingerprint'
import { useOperatorScript } from '@/composables/useOperatorScript'
import { useMembershipDetails } from '@/composables/useMembershipDetails'
import OperatorScriptModal from '@/components/Common/OperatorScriptModal.vue'
import LoaderOverlay from '@/components/Common/LoaderOverlay.vue'
import { featureFlagsStore } from '@/stores/featureFlagsStore'

defineOptions({
    name: 'Factory'
})

const { t } = useI18n();

const store = useRiskStore()
const quoteErrors = useQuoteErrors()
// const { productOutput, riskData, quoteId, productId, action, agentId, ownerId } = storeToRefs(store)
const { riskData, coverDetails, assumptions } =
  storeToRefs(store)
const action = ref(window.quoteAction as string);
// Refer a friend descoped (2026-10-20) — const isReferral = (window as any).isReferral === true;
const isAgent = (window as any).isAgent;
const agentId = (window as any).agentId;
const ownerId = (window as any).modelData ? (window as any).modelData.OwnerId : null;
let quoteId = (window as any).quoteId as string;

// Customersite chrome: coarse Markel Direct progress tracker (kept out of the shared schema.ts).
// Teleported into the header strip by QuoteNav; granular quote steps collapse into four stages.
const trackerStages = [
    { label: 'About You', steps: ['risk-questions'] },
    { label: 'Your Quote', steps: ['quote-display', 'registration', 'assumptions', 'confirmation'] },
    { label: 'Payment', steps: ['payment'] },
    { label: "You're Covered", steps: ['complete'] },
];

const isReview = computed(() => {
  const a = (action.value ?? '').toLowerCase()
  return a.includes('review') || a.includes('mtareview') || a.includes('renewalreview')
})
store.isReviewMode = isReview.value

let tabController = ref([] as any);
let loading = ref(false);

// Resume-on-reload: the step the journey should open on. The quote is bound to the server session
// (no client-side quote id until a reload), and sessionStorage is already per-tab = one quote per
// tab, so we key by product (stable, present from the first paint) rather than quote id. Persisted
// on every step change (see useTabController); Factory resolves it once the schema + risk data are
// loaded, re-rating first if the saved step is a post-quote page whose output isn't in memory.
const stepStorageKey = computed(() => `single-scheme-quote-step:${props.productId}`);
const resumeTabId = ref<string>('');

const props = defineProps<{
    productId: string
}>();

const { config: productConfig } = useQuoteProductConfig(computed(() => props.productId));

// The quote-display layout is chosen per tenant from the product registry
const quoteDisplayComponent = computed(() => {
    const cfg = productConfig.value;
    return cfg?.quoteDisplay === 'singleScheme' ? QuoteTabSingleScheme : QuoteTab;
});
const isSingleScheme = computed(() => productConfig.value?.quoteDisplay === 'singleScheme');

type NavExpose = {
  goBack: () => void
  goNext: () => void
  show: (id: string) => void
  goLast: () => void,
  currentTabId: string
}

const navRef = ref<NavExpose | null>(null)

const tenant = ref<string | null>(null);

const formData = ref({} as any);

const additionalFormData = ref({
} as any);

// This will hold the imported file content (e.g. the object containing formSchema)
const currentTenantConfig = ref<any>(null);
const formSchema = ref({} as any);
const riskTemplate = ref({} as any);
const mapToRisk = ref<((data: any, opts: any) => any) | null>(null);

// Everything the journey needs before the first step can render: the tenant
// schema chunk, its risk template and mapper, and the step list.
const journeyReady = computed(() => !loading.value
    && !!currentTenantConfig.value
    && !!formSchema.value
    && !!mapToRisk.value
    && tabController.value.length > 0);

/*
 * Vueform builds a whole step in one synchronous pass - a few seconds on the
 * larger tenants - and that blocks the main thread, so a spinner rendered in the
 * same tick would never reach the screen. Hold the very first step back for a
 * frame so the spinner paints, then let the form mount behind it.
 *
 * Only the first step is gated. Moving between steps already looks better
 * without it: the browser can't repaint while the next step builds, so the step
 * you came from (and its saving overlay) stays up until the new one is ready.
 * Forcing a paint there would just swap that for a spinner you have to watch.
 */
const stepReady = ref(false);
let stepFrame = 0;
let stepTimer: ReturnType<typeof setTimeout> | 0 = 0;

const cancelStepFrame = () => {
    if (stepFrame) cancelAnimationFrame(stepFrame);
    if (stepTimer) clearTimeout(stepTimer);
    stepFrame = 0;
    stepTimer = 0;
};

watch(() => (journeyReady.value ? navRef.value?.currentTabId : null), (tabId) => {
    // Once the first step is through, this is inert - later steps mount straight away.
    if (!tabId || stepReady.value || stepFrame || stepTimer) return;

    if (typeof requestAnimationFrame !== 'function') {
        stepReady.value = true;
        return;
    }
    const markReady = () => { cancelStepFrame(); stepReady.value = true; };
    // Two frames: the first still runs before the spinner is painted, the second
    // after it, which is when the form can safely take the thread.
    stepFrame = requestAnimationFrame(() => {
        stepFrame = requestAnimationFrame(markReady);
    });
    // Fallback: rAF is paused in a hidden/background tab, which would otherwise wedge the
    // "Loading your questions" spinner until the tab is focused. A timer still fires there.
    stepTimer = setTimeout(markReady, 250);
}, { immediate: true });

onBeforeUnmount(cancelStepFrame);

const { appContext } = getCurrentInstance()!
const ffStore = featureFlagsStore(appContext.app._context.provides.pinia as Pinia)
const tempOperatorScriptVue = ref(false)

// Server-evaluated flag: rate the first quote display on initial questions only (additional answers stripped).
const firstDisplayInitialOnly = (window as any).featureFlags?.firstDisplayInitialOnly === true

const operatorScript = useOperatorScript()
const { membershipType } = useMembershipDetails()

const operatorScripts: Record<string, { object: string, action: string, subobject: string, event: string, parameters: any, callback: (evt: { text: string; success: boolean; hasOp: boolean }) => void }> = {
  'risk-questions': { object: 'Quote', action: 'Create', subobject: 'AnyType', event: 'Load', parameters: null, callback: (evt) => { console.info(evt.text, evt.success); } },
  'confirmation': { object: 'Quote', action: 'Create', subobject: 'QuoteDisplaySelected', event: 'Submit', parameters: null, callback: (evt) => { console.info(evt.text, evt.success); } },
  }

function showOperatorScriptForTab(tabId: string) {
  if (!tempOperatorScriptVue.value || membershipType.value === 'broker') return
  const config = operatorScripts[tabId]
  if (!config) return
  operatorScript.show(config.object, config.action, config.subobject, config.event, config.parameters, config.callback)
}

onMounted(async () => {
  await ffStore.initFeatureFlags()
  tempOperatorScriptVue.value = await ffStore.isFeatureEnabled('temp-operator-script-vue')

  // Only show the operator script modal for non-agent users (e.g., brokers or customers),
  // If they are logged in as an agent, we don't want to show the operator script modal. and then the showOperatorscript function
  // has logic in to stop it showing for when the broker id is used.
  if (isAgent) return
  let fired = false
  watch(membershipType, (type) => {
    if (!type || fired) return
    fired = true
    showOperatorScriptForTab('risk-questions')
  }, { immediate: true })
})

// Resume step: honour the saved step; a post-quote step re-rates first (rating output is absent after
// a reload), falling back to the first step if that fails.
const resolveResumeStep = async () => {
    const steps = (tabController.value as Array<{ id: string; hide?: boolean }>).filter((s) => !s.hide);
    const firstId = steps[0]?.id ?? '';
    resumeTabId.value = firstId;

    if (isReview.value || !stepStorageKey.value) return;

    // A brand-new quote has no server id yet: start fresh and drop any step saved by a previous
    // quote that used this tab, so it can't leak into the new journey.
    if (!quoteId) {
        try { sessionStorage.removeItem(stepStorageKey.value); } catch { /* storage unavailable */ }
        return;
    }

    let saved = '';
    try { saved = sessionStorage.getItem(stepStorageKey.value) || ''; } catch { saved = ''; }
    // Step from the path segment (/retail-shops/quote/<step>); aliases about-you = first, your-quote = display.
    try {
        const segs = window.location.pathname.split('/').filter(Boolean);
        const p = segs.length > 1 && segs[segs.length - 2] === 'quote' ? segs[segs.length - 1] : '';
        const requested = p === 'about-you' ? firstId : p === 'your-quote' ? 'quote-display' : (steps.some((s) => s.id === p) ? p : '');
        if (requested) { saved = requested; try { sessionStorage.setItem(stepStorageKey.value, requested); } catch { /* storage unavailable */ } }
    } catch { /* no path segment */ }
    const savedIdx = steps.findIndex((s) => s.id === saved);
    if (savedIdx <= 0) return; // unknown, or already the first step

    const quoteIdx = steps.findIndex((s) => s.id === 'quote-display');
    const needsOutput = quoteIdx !== -1 && savedIdx >= quoteIdx;

    if (needsOutput && !store.productOutput) {
        try {
            const risk = riskData.value ? JSON.parse(JSON.stringify(riskData.value)) : {};
            // Rebuilding productOutput on resume is a neutral re-rate (page 0); initial-only when resuming onto the quote display.
            const resumeInitialOnly = saved === 'quote-display' && firstDisplayInitialOnly;
            const { doneQuote } = await DoQuote(action.value, props.productId, quoteId, risk, agentId, ownerId, 0, resumeInitialOnly);
            if (doneQuote) {
                store.setPayload({ ...(store.payload ?? {}), productOutput: doneQuote, action: action.value ?? '' });
                if (resumeInitialOnly) store.cacheInitialOnly(doneQuote, fingerprintRisk(store.riskData, store.additionalOnlyKeys));
                resumeTabId.value = saved;
            }
        } catch (e) {
            console.error('Resume re-rate failed; starting from the first step:', e);
        }
        return;
    }

    resumeTabId.value = saved;
};

const getTenantData = async () => {
    loading.value = true;
    try {
    const activeProductConfig = productConfig.value;
    if (!activeProductConfig) {
      throw new Error(`No quote product configuration found for product ${props.productId}`);
    }

    tenant.value = activeProductConfig.schemaDirectory;
        currentTenantConfig.value = null; // Reset before loading new config
    const tenantModule = await activeProductConfig.loadSchema();
        currentTenantConfig.value = tenantModule.default;
        
        // Extract all required tenant exports
        formSchema.value = currentTenantConfig.value.getFormFields();
        riskTemplate.value = currentTenantConfig.value.getRiskTemplate();
        // Keys exclusive to the additional step — excluded from the initial-only cache fingerprint.
        store.setAdditionalOnlyKeys(computeAdditionalOnlyKeys(formSchema.value));
        // Single-scheme review relabels the confirmation step "Review Quote" in the stepper.
        const builtSteps = (currentTenantConfig.value.steps as any[]).map((s: any) =>
            (isReview.value && isSingleScheme.value && s.id === 'confirmation') ? { ...s, name: 'Review Quote' } : s);
        // Customersite-only: a Registration interstitial between the quote display and additional info
        // (Markel Direct). Kept out of the shared schema.ts — agents don't register customers — and
        // injected here, mirroring how the coarse tracker stays out of the schema. Skipped when the
        // customer is already logged in (a seeded Customer record) — they don't need to register.
        const isLoggedIn = (window as any).isAuthenticated === true;
        if (isCustomerSite && !isReview.value && !isLoggedIn && !builtSteps.some((s: any) => s.id === 'registration')) {
            const quoteIdx = builtSteps.findIndex((s: any) => s.id === 'quote-display');
            if (quoteIdx !== -1) builtSteps.splice(quoteIdx + 1, 0, { id: 'registration', name: 'Registration', disabled: true, hide: false });
        }
        tabController.value = builtSteps;
        mapToRisk.value = currentTenantConfig.value.mapToRisk;
        formData.value = generateEmptyModel(formSchema.value);
        generateAdditionalFormData();

        // Load saved risk data when resuming an existing quote
        let savedRiskDataApplied = false;
        if (quoteId) {
            try {
                const url = `/retail-shops/getriskdata?id=${quoteId}&action=${action.value}`;
                const response = await fetch(url);
                if (response.ok) {
                    const data = await response.json();
                    const rawRiskData = data?.RiskData ?? data?.riskData;
                    if (rawRiskData && rawRiskData !== '') {
                        const rawParsed = typeof rawRiskData === 'string' ? JSON.parse(rawRiskData) : rawRiskData;
                        // Reverse the save-time shaping so complex fields reload into the form correctly.
                        const reshaped = currentTenantConfig.value?.mapRiskToForm?.(rawParsed) ?? rawParsed;

                        // Repair values saved against an option list whose casing has since
                        // changed. After mapRiskToForm, so the paths match the schema.
                        const { data: parsed, changes } = canonicaliseSchemaValues(formSchema.value, reshaped);
                        const changedPaths = Object.keys(changes);
                        if (changedPaths.length) {
                            console.info(`[quote] repaired ${changedPaths.length} saved value(s) whose casing no longer matches their options:`, changes);
                        }

                        // Review: ensure a ShadowData copy of the original. Edit: strip any stale
                        // ShadowData so it's rebuilt fresh next time the quote enters review.
                        if (store.isReviewMode) {
                            store.ensureShadow(parsed);
                        } else {
                            store.stripShadow(parsed);
                        }
                        store.setFormDataSection('riskData', parsed);
                        savedRiskDataApplied = true;
                    }

                    // Seed the saved rating output so a resume to a post-quote step (e.g. a Reviewed
                    // quote opening on its confirmed/buy screen) shows the saved figures without a
                    // re-rate
                    const savedOutput = data?.productOutput;
                    if (savedOutput && Array.isArray(savedOutput.ReturnSchemes) && savedOutput.ReturnSchemes.length) {
                        store.setPayload({ ...(store.payload ?? {}), productOutput: savedOutput, action: action.value ?? '' });
                    }
                }
            } catch (e) {
                console.error('Failed to load saved risk data for resume:', e);
            }
        }

        // New business (no saved risk data): prefill proposer fields from the
        // customer record supplied on window.modelData (see VueQuoteComponent),
        // and seed the membership id. The server (GetMembershipId) already
        // resolves this to the user's claim / agency reference, or "cet1088"
        // when there is no membership number, so we just surface that value.
        if (!savedRiskDataApplied) {
            const customer = (window as any).modelData?.Customer;
            const prefill = currentTenantConfig.value?.mapCustomerPrefill?.(customer) ?? {};

            const membershipId = (window as any).modelData?.MembershipId;
            if (membershipId) {
                prefill.MembershipId = membershipId;
            }

            // Activity handover from Markel Direct: preselect the shop + dedicated-premises.
            const handover = currentTenantConfig.value?.mapHandoverPrefill?.((window as any).handoverActivity);
            if (handover) Object.assign(prefill, handover);

            if (Object.keys(prefill).length) {
                store.setFormDataSection('riskData', prefill);
            }
        }

        // Single-scheme review opens on the first page; seed the productOutput from the saved
        // scheme output so the quote display shows saved values (no re-rate, so overrides/endorsements survive).
        if (isReview.value && isSingleScheme.value && !store.productOutput) {
            const rawOutput = (window as any).modelData?.SchemeOutput;
            if (rawOutput) {
                try {
                    const parsedOutput = typeof rawOutput === 'string' ? JSON.parse(rawOutput) : rawOutput;
                    if (parsedOutput) store.setPayload({ ...(store.payload ?? {}), productOutput: parsedOutput, action: action.value ?? '' });
                } catch (e) {
                    console.error('Failed to seed review scheme output:', e);
                }
            }
        }

        await resolveResumeStep();
    } catch (error) {
        console.error(`Error loading tenant configuration for ${tenant.value}:`, error);
        currentTenantConfig.value = null;
    } finally {
        loading.value = false;
    }
};

watch(() => props.productId, async (newPropertyId) => {
    if (!newPropertyId) return;

    getTenantData();
}, { immediate: true }); // immediate: true runs this logic immediately on mount


const handleSaveAndNext = async (submitData: any) => {
  const { mergedData, schemaKey, formModel } = submitData

  const effectiveAgentId = mergedData?.AgentId || agentId
  const effectiveMembershipId = mergedData?.MembershipId || (window as any).modelData?.MembershipId

  const mappedRiskData = currentTenantConfig.value?.mapAdditionalRisk?.(mergedData, {
    productId: props.productId ?? undefined,
    action: action,
    agentId: effectiveAgentId,
    membershipId: effectiveMembershipId,
  }) ?? mergedData

  if (schemaKey === 'coverDetails') {
    store.setFormDataSection('coverDetails', formModel ?? mappedRiskData)
  } else if (schemaKey === 'assumptions') {
    store.setFormDataSection('assumptions', formModel ?? mappedRiskData)
  } else {
    store.setFormDataSection('riskData', mappedRiskData)
  }

  await SaveQuoteStage(
    action.value,
    props.productId,
    quoteId,
    mappedRiskData,
    effectiveAgentId
  )

  window.hideLoader()
  navRef.value?.goNext()
}

// A step runs DoQuote on submit when the next visible step shows a quote — either the
// quote-display, or a step flagged `requoteOnEnter`
const triggersQuote = (stepId: string) => {
  const items = tabController.value as Array<{ id: string; hide?: boolean; requoteOnEnter?: boolean }>
  const idx = items.findIndex((t) => t.id === stepId)
  if (idx === -1) return false
  const next = items.slice(idx + 1).find((t) => !t.hide)
  return next?.id === 'quote-display' || next?.requoteOnEnter === true
}

// A step whose schema has exactly one object/group root "owns" that namespace
// (coverDetails → CoverDetails, assumptions → Assumptions). Other steps may carry
// hidden *mirrors* of that namespace — e.g. the risk-questions renovation block
// mirrors CoverDetails.* to prefill the Cover Details page — and those mirrors
// must never let the hidden-field save sweep delete the owning step's answers.
// So each step is told which roots the other dedicated steps own, and skips them.
const objectRootsFor = (schemaKey: string): string[] => {
  const stepSchema = formSchema.value?.[schemaKey]
  if (!stepSchema || typeof stepSchema !== 'object') return []
  return Object.keys(stepSchema).filter((key) => {
    const def = (stepSchema as Record<string, any>)[key]
    return def && typeof def === 'object' && (def.type === 'object' || def.type === 'group')
  })
}

const ownedRootBySchemaKey = computed<Record<string, string>>(() => {
  const owners: Record<string, string> = {}
  for (const schemaKey of Object.keys(formSchema.value ?? {})) {
    const roots = objectRootsFor(schemaKey)
    if (roots.length === 1) owners[schemaKey] = roots[0]
  }
  return owners
})

const foreignRootsFor = (schemaKey: string): string[] =>
  Object.entries(ownedRootBySchemaKey.value)
    .filter(([ownerKey]) => ownerKey !== schemaKey)
    .map(([, root]) => root)

// Every question step takes the same props; only the step id and its schema slice vary.
const questionSetProps = (tab: { id: string; schemaKey: string }) => ({
  tabId: tab.id,
  schemaKey: tab.schemaKey,
  formSchema: formSchema.value[tab.schemaKey],
  riskTemplate: riskTemplate.value,
  // Non-null: the root v-if only renders the steps once mapToRisk has loaded.
  mapToRisk: mapToRisk.value!,
  formData: formData.value,
  additionalFormData: additionalFormData.value,
  foreignRoots: foreignRootsFor(tab.schemaKey),
})

const handleInitialSubmit = async (submitData: any, getQuote: any = false) => {
  const { mergedData, schemaKey } = submitData
  // Rating/validation page: additional questions = 2, initial = 1.
  const page = schemaKey === 'assumptions' ? 2 : 1

  // Apply cross-step mutations defined per-tenant (e.g. syncing assumption answers
  // back to risk question fields that live in a different Vueform instance).
  //currentTenantConfig.value?.resolveAssumptions?.(mergedData)

  // Prefer the agent chosen during the quote journey (the broker lookup writes it into
  // the risk data) over the page-level window.agentId, so the selection actually reaches
  // the saved quote — and later Policy.AgentId, which drives broker commission.
  const effectiveAgentId = mergedData?.AgentId || agentId

  // Same reasoning for the member number: on the broker journey MembershipId is a hidden
  // conditional field that gets stripped from mergedData, so fall back to the server-resolved
  // value on window.modelData (claim → agency reference → cet1088) to keep it in the risk data.
  const effectiveMembershipId = mergedData?.MembershipId || (window as any).modelData?.MembershipId

  const mappedRiskData = currentTenantConfig.value?.mapAdditionalRisk?.(mergedData, {
    productId: props.productId ?? undefined,
    action: action,
    agentId: effectiveAgentId,
    membershipId: effectiveMembershipId,
  }) ?? mergedData

  // Single-scheme tenants edit cover selections / limits on the quote display, not in
  // the questionnaire, so they're absent from mergedData. Carry them over from the current risk data
  // to preserve on requote
  if (isSingleScheme.value) {
    const existingCovers = riskData.value?.Covers
    if (Array.isArray(existingCovers) && existingCovers.length) {
      mappedRiskData.Covers = existingCovers
    }
  }

  store.setFormDataSection('riskData', mappedRiskData)

  // Distribute mappedRiskData back into formData so every tab sees the latest state.
  // formData[schemaKey] is keyed by element names (e.g. RiskQuestions, FlatRoofDetails)
  // which are also top-level keys in mappedRiskData, so we can match them directly.
  const updatedFormData: Record<string, any> = {}
  for (const [schemaKey, stepSchema] of Object.entries(formSchema.value)) {
    const current = formData.value[schemaKey] ?? {}
    const patch: Record<string, any> = {}
    for (const elementKey of Object.keys(stepSchema as Record<string, any>)) {
      if (mappedRiskData[elementKey] !== undefined) {
        patch[elementKey] = mappedRiskData[elementKey]
      }
    }
    updatedFormData[schemaKey] = { ...current, ...patch }
  }
  formData.value = updatedFormData


  try {
    if (getQuote) {
      // Quote step: getquote persists the risk data AND rates in one call.
      const { doneQuote } = await DoQuote(
        action.value,
        props.productId,
        quoteId,
        mappedRiskData,
        effectiveAgentId,
        ownerId,
        page,
        page === 1 && firstDisplayInitialOnly   // page 1 heads to the quote-display: rate on initial questions only
      )

      if (doneQuote) {
        quoteErrors.clear()
        store.setPayload({
          ...(store.payload ?? {}),
          productOutput: doneQuote,
          action: action.value ?? '',
        })
        // page 1 output is initial-only — cache it for an instant reload on a later return to the display.
        if (page === 1 && firstDisplayInitialOnly) store.cacheInitialOnly(doneQuote, fingerprintRisk(store.riskData, store.additionalOnlyKeys))
        window.hideLoader()
        navRef.value?.goNext()
      } else {
        // Rating returned nothing usable — stay on the step and surface a generic error banner.
        window.hideLoader()
        quoteErrors.setServer(null)
      }
    } else {
      // Intermediate step: persist progress only.
      await SaveQuoteStage(action.value, props.productId, quoteId, mappedRiskData, effectiveAgentId)
      window.hideLoader()
      navRef.value?.goNext()
    }
  }
  catch (e) {
    console.error('Error during quote submission:', e)
    window.hideLoader()
    // Stay on the current step; show server-side validation / error text at the top (Option A/B).
    // 5xx passes through as the standard message — the raw error body is never shown.
    quoteErrors.setServer(e instanceof ApiError ? e.body : null, e instanceof ApiError ? e.status : 500)
  }
}

// Returning to the first quote display (any route: Back, stepper, browser back; oldId skips initial load):
// reload the cached initial-only quote when inputs are unchanged, else re-rate behind the skeleton + loader.
watch(() => navRef.value?.currentTabId, async (id, oldId) => {
  if (!oldId || id !== 'quote-display') return
  if (!firstDisplayInitialOnly || !isSingleScheme.value || riskData.value?.AgreedToTermsAndConditions !== true) return

  const fingerprint = fingerprintRisk(riskData.value, store.additionalOnlyKeys)
  // Initial inputs unchanged → reload the cached initial-only output instantly (no skeleton, no loader).
  if (store.initialOnlyOutput && store.initialOnlyFingerprint === fingerprint) {
    store.setPayload({ ...(store.payload ?? {}), productOutput: store.initialOnlyOutput, action: action.value ?? '' })
    return
  }
  // Changed/none cached → re-rate; show the skeleton + loader (set synchronously so no stale view flashes).
  store.setInitialOnlyRerating(true)
  window.showLoader('Please wait', 'Retrieving your quote', false, null, true)
  try {
    const risk = riskData.value ? JSON.parse(JSON.stringify(riskData.value)) : {}
    const { doneQuote } = await DoQuote(action.value, props.productId, quoteId, risk, agentId, ownerId, 0, true)
    if (doneQuote) {
      store.setPayload({ ...(store.payload ?? {}), productOutput: doneQuote, action: action.value ?? '' })
      store.cacheInitialOnly(doneQuote, fingerprintRisk(store.riskData, store.additionalOnlyKeys))
    }
  } catch (e) {
    console.error('Back-nav initial-only re-rate failed:', e)
  } finally {
    store.setInitialOnlyRerating(false)
    window.hideLoader()
  }
})

const generateAdditionalFormData = () => {
  const data =  {
    AdditionalFormData: {
        IsCeta: !isAgent,
        isCetaMembershipId: null,
        isReview: isReview.value,
        currentStep: 'assumptions',
        isMTA: action.value.toLowerCase().includes('mta'),
        isRNL: action.value.toLowerCase().includes('renewal'),
        isMSM: !!(window as any).modelData?.IsMoneySuperMarketUser,
        isAGG: !!(window as any).modelData?.IsAggregatorUser,
        quoteCreatedOnCustomerSite: !!(window as any).modelData?.QuoteCreatedOnCustomerSite,
        isOnAccount: !!(window as any).modelData?.IsOnAccount,
        // Agent commission rate the policy is already on. Only populated for an MTA, so null
        // means "no prior rate" rather than zero commission.
        policyCommissionRate: (window as any).modelData?.PolicyCommissionRate ?? null,
        membershipType: null,
        paymentType: null,
    },
  }

  additionalFormData.value = data;
};

</script>

<style scoped>
</style>
